import { ArrowRight, BookPlus, FileText, Flame, Play, Target } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { BookCover, BookProgress, Button, EmptyState, Modal, StatCard } from '../../components/ui';
import type { Book, HomeHighlightContent } from '../../domain/entities/types';
import { useBookForecast, useBookStatistics, useCurrentReads, useDailyStatisticsComparison, useGoal, useHomeHighlightContent, useSettings, useStatistics } from '../../hooks/useData';
import { readingService } from '../../services/readingService';
import { formatDuration, getProgressLabel } from '../../utils/core';
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useToast } from '../../app/Providers';
import { DailyPagesComparisonChart } from '../statistics/DailyPagesComparisonChart';

const MAX_VISIBLE_DOTS = 7;

export const HomeHighlightCard = ({
  content,
  enabled
}: {
  content: HomeHighlightContent | undefined;
  enabled: boolean;
}) => {
  const quoteRef = useRef<HTMLQuoteElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [readingOpen, setReadingOpen] = useState(false);
  const visible = Boolean(enabled && content?.published && content.text.trim());

  useLayoutEffect(() => {
    const quote = quoteRef.current;
    if (!visible || !quote) {
      setIsTruncated(false);
      return;
    }

    const measure = () => setIsTruncated(quote.scrollHeight > quote.clientHeight + 1);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    observer?.observe(quote);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [content?.text, visible]);

  if (!visible || !content) return null;
  return (
    <>
      <aside className="home-highlight" aria-label={content.title || 'Destaque da Home'}>
        {content.title && <span className="home-highlight__eyebrow">{content.title}</span>}
        <blockquote ref={quoteRef}>{content.text}</blockquote>
        {(content.reference || isTruncated) && <div className="home-highlight__meta">
          {content.reference && <cite>{content.reference}</cite>}
          {isTruncated && <button className="home-highlight__more" type="button" aria-haspopup="dialog" aria-expanded={readingOpen} onClick={() => setReadingOpen(true)}>Ver mais</button>}
        </div>}
      </aside>
      {(isTruncated || readingOpen) && <Modal open={readingOpen} title={content.title || 'Destaque da Home'} onClose={() => setReadingOpen(false)}>
        <article className="home-highlight-modal">
          <blockquote className="home-highlight-modal__quote">{content.text}</blockquote>
          {content.reference && <cite className="home-highlight-modal__reference">{content.reference}</cite>}
          <div className="home-highlight-modal__actions">
            <Button variant="secondary" aria-label="Fechar leitura completa" onClick={() => setReadingOpen(false)}>Fechar</Button>
          </div>
        </article>
      </Modal>}
    </>
  );
};

export const BiblePagesRead = ({ book, pagesRead }: { book: Book; pagesRead: number | undefined }) => {
  if (book.progress.kind !== 'bible_chapters' || pagesRead === undefined) return null;
  const total = book.progress.totalPages;
  return (
    <span className="current-read-card__bible-pages">
      <span className="current-read-card__page-icon" aria-hidden="true"><FileText /></span>
      <span>{pagesRead.toLocaleString('pt-BR')}{total !== undefined ? ` de ${total.toLocaleString('pt-BR')}` : ''} {pagesRead === 1 && total === undefined ? 'página' : 'páginas'}</span>
    </span>
  );
};

const CurrentReadCard = ({ book, position, total }: { book: Book; position: number; total: number }) => {
  const forecast = useBookForecast(book);
  const statistics = useBookStatistics(book.progress.kind === 'bible_chapters' ? book.id : undefined);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const start = async () => {
    if (book.progress.kind === 'bible_chapters') {
      navigate(`/books/${book.id}?start=1`);
      return;
    }
    try { const session = await readingService.startSession(book.id); navigate(`/session/${session.id}`); }
    catch (error) { showToast(error instanceof Error ? error.message : 'Não foi possível iniciar.', 'error'); }
  };

  return (
    <article
      className="current-read-card"
      data-carousel-slide={total > 1 ? position - 1 : undefined}
      role={total > 1 ? 'group' : undefined}
      aria-roledescription={total > 1 ? 'slide' : undefined}
      aria-label={total > 1 ? `Livro ${position} de ${total}: ${book.title}` : undefined}
    >
      <Link to={`/books/${book.id}`} className="current-read-card__book">
        <div className="current-read-card__top">
          <BookCover book={book} size="large" priority={position === 1} />
          <div className="current-read-card__content">
            <span className="eyebrow">Continue sua leitura</span>
            <h2 title={book.title}>{book.title}</h2>
            <p title={book.author}>{book.author}</p>
          </div>
        </div>
        <div className="current-read-card__progress">
          <div className="current-read-card__progress-copy">
            {book.progress.kind === 'pages' && <span className="current-read-card__page-icon" aria-hidden="true"><FileText /></span>}
            <strong>{getProgressLabel(book.progress)}</strong>
            <BiblePagesRead book={book} pagesRead={statistics?.pages} />
          </div>
          <BookProgress book={book} />
        </div>
      </Link>
      <div className="current-read-card__actions">
        <Button onClick={start}><Play /> Iniciar leitura</Button>
        <Button variant="secondary" onClick={() => navigate(`/books/${book.id}?progress=1`)}>Registrar leitura</Button>
      </div>
      {forecast && <div className="forecast"><Flame aria-hidden="true" /><span>No seu ritmo, você termina em cerca de <strong>{forecast.amount} {forecast.amount === 1 ? 'dia' : 'dias'}</strong>.</span></div>}
    </article>
  );
};

const CurrentReadsCarousel = ({ reads }: { reads: Book[] }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollFrameRef = useRef<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const visibleActiveIndex = Math.min(activeIndex, reads.length - 1);

  useEffect(() => {
    return () => {
      if (scrollFrameRef.current !== null) window.cancelAnimationFrame(scrollFrameRef.current);
    };
  }, []);

  const updateActiveIndex = () => {
    if (scrollFrameRef.current !== null) return;
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      const track = trackRef.current;
      if (!track) return;
      const slides = [...track.querySelectorAll<HTMLElement>('[data-carousel-slide]')];
      const viewportCenter = track.scrollLeft + track.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - viewportCenter);
        if (distance < closestDistance) { closestDistance = distance; closestIndex = index; }
      });
      setActiveIndex((current) => current === closestIndex ? current : closestIndex);
    });
  };

  const scrollToSlide = (index: number) => {
    const track = trackRef.current;
    const slide = track?.querySelector<HTMLElement>(`[data-carousel-slide="${index}"]`);
    if (!track || !slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2,
      behavior: 'smooth'
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    scrollToSlide(Math.max(0, Math.min(reads.length - 1, visibleActiveIndex + direction)));
  };

  return (
    <section className="current-reads-carousel" aria-label="Livros em leitura" aria-roledescription="carrossel">
      <div
        ref={trackRef}
        className="current-reads-carousel__track"
        tabIndex={0}
        onScroll={updateActiveIndex}
        onKeyDown={handleKeyDown}
      >
        {reads.map((book, index) => <CurrentReadCard key={book.id} book={book} position={index + 1} total={reads.length} />)}
      </div>
      <div className="current-reads-carousel__pagination">
        <span className="sr-only" aria-live="polite" aria-atomic="true">Livro {visibleActiveIndex + 1} de {reads.length}: {reads[visibleActiveIndex]?.title}</span>
        {reads.length <= MAX_VISIBLE_DOTS ? (
          <div className="current-reads-carousel__dots" aria-hidden="true">
            {reads.map((book, index) => <span key={book.id} className={index === visibleActiveIndex ? 'is-active' : ''} />)}
          </div>
        ) : <span className="current-reads-carousel__count" aria-hidden="true">{visibleActiveIndex + 1} de {reads.length}</span>}
      </div>
    </section>
  );
};

export const HomePage = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth();
  const currentDay = now.getDate();
  const reads = useCurrentReads();
  const month = useStatistics('month');
  const year = useStatistics('year');
  const goal = useGoal(currentYear);
  const settings = useSettings();
  const homeHighlight = useHomeHighlightContent();
  const [selectedDailyMonth, setSelectedDailyMonth] = useState(() => new Date(currentYear, currentMonthIndex, 1));
  const selectedDailyYear = selectedDailyMonth.getFullYear();
  const selectedDailyMonthIndex = selectedDailyMonth.getMonth();
  const selectedDailyIsCurrent = selectedDailyYear === currentYear && selectedDailyMonthIndex === currentMonthIndex;
  const selectedDailyDay = selectedDailyIsCurrent
    ? currentDay
    : new Date(selectedDailyYear, selectedDailyMonthIndex + 1, 0).getDate();
  const dailyComparison = useDailyStatisticsComparison(selectedDailyMonth);
  const selectedMonthStatistics = useStatistics('month', selectedDailyMonth);
  const finishedBooks = year?.finishedBooks ?? 0;
  const bookTarget = goal?.bookTarget;
  const goalProgress = bookTarget ? Math.min(100, (finishedBooks / bookTarget) * 100) : undefined;
  return (
    <div className="page home-page">
      <HomeHighlightCard content={homeHighlight} enabled={settings?.showHomeHighlight ?? true} />

      <div className="home-desktop-layout">
        <div className="home-reading-column">
          {!reads.length ? (
            <EmptyState title="Sua estante está esperando" action={<Link className="button button--primary" to="/books/new"><BookPlus /> Adicionar primeiro livro</Link>}>
              Cadastre o que você está lendo e transforme cada sessão em parte da sua história.
            </EmptyState>
          ) : reads.length === 1
            ? <CurrentReadCard book={reads[0]!} position={1} total={1} />
            : <CurrentReadsCarousel reads={reads} />}
        </div>

        <aside className="home-summary-column" aria-label="Resumo da leitura">
          <Link to="/settings" className="goal-card">
            <div className="goal-card__icon"><Target /></div>
            <div className="goal-card__content">
              <span>Meta {currentYear}</span>
              <strong>{finishedBooks} de {bookTarget ?? '—'} livros</strong>
              {goalProgress !== undefined && <div className="goal-card__track" role="progressbar" aria-label={`Progresso da meta de ${currentYear}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(goalProgress)}><span style={{ width: `${goalProgress}%` }} /></div>}
            </div>
            <ArrowRight />
          </Link>

          <section className="section-block home-month-section">
            <div className="section-heading home-month-heading"><h2>Este mês</h2><span>Seu ritmo recente</span></div>
            <div className="stats-grid stats-grid--three home-month-stats">
              <StatCard label="Páginas" value={month?.trackedPages ?? 0} />
              <StatCard label="Tempo" value={formatDuration(month?.durationMs ?? 0)} />
              <StatCard label="Dias ativos" value={month?.activeDays ?? 0} />
            </div>
          </section>
        </aside>

        <div className="home-month-chart">
          <DailyPagesComparisonChart
            referenceDate={new Date(selectedDailyYear, selectedDailyMonthIndex, selectedDailyDay)}
            currentDailyPages={dailyComparison.currentPages}
            previousDailyPages={dailyComparison.previousPages}
            currentDailyDurationMs={dailyComparison.currentDurationMs}
            previousDailyDurationMs={dailyComparison.previousDurationMs}
            hasHistoricalPages={(selectedMonthStatistics?.historicalPages ?? 0) > 0}
            maxReferenceDate={now}
            onReferenceMonthChange={setSelectedDailyMonth}
          />
        </div>
      </div>
    </div>
  );
};
